/**
 * REST klient.
 *
 * Tokenlar `HttpOnly` cookie'da saqlanadi (`sf_access` / `sf_refresh`), shuning
 * uchun so'rovlar `credentials: "include"` bilan yuboriladi va JS tokenni
 * o'qimaydi (XSS'da o'g'irlanmaydi). O'zgartiruvchi metodlar uchun
 * double-submit CSRF: `sf_csrf` cookie'si `X-CSRF-Token` sarlavhasiga qo'yiladi.
 */

/* Standart holatda same-origin («/api/v1») — cookie SameSite=Lax bo'lgani uchun
   so'rov Vite yoki nginx proksisi orqali backendga uzatiladi. */
export const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly payload: unknown;

  constructor(status: number, message: string, code = "", payload: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  /** Tarmoq uzilgan yoki server javob bermadi. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

/**
 * Xatolik javobidan xabar va kodni ajratib oladi.
 *
 * Backend ikki xil ko'rinishda javob beradi:
 *   * `{"error": {"code", "message", "details", "request_id"}}` — istisno
 *     `apps.common.exceptions.smart_exception_handler` orqali o'tganda
 *     (masalan `mfa_required`, `rate_limited`);
 *   * `{"detail": "…"}` — view javobni o'zi qaytarganda (`Response({...})`).
 *
 * Ikkalasi ham qo'llab-quvvatlanishi shart: aks holda `code` yo'qoladi va
 * kirish sahifasi MFA bosqichiga o'ta olmay, foydalanuvchi umumiy
 * «So'rov bajarilmadi (401)» xabarini ko'radi.
 */
function readError(payload: unknown, status: number): { message: string; code: string } {
  const fallback = `So'rov bajarilmadi (${status}).`;
  if (typeof payload !== "object" || payload === null) return { message: fallback, code: "" };

  const body = payload as Record<string, unknown>;
  const envelope = body.error;
  if (typeof envelope === "object" && envelope !== null) {
    const error = envelope as Record<string, unknown>;
    return {
      message: typeof error.message === "string" && error.message ? error.message : fallback,
      code: typeof error.code === "string" ? error.code : "",
    };
  }

  return {
    message: typeof body.detail === "string" && body.detail ? body.detail : fallback,
    code: typeof body.code === "string" ? body.code : "",
  };
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Til backendga `Accept-Language` orqali uzatiladi (4 tilli kontent uchun). */
  lang?: string;
  /**
   * Javobni yuklash ulushi (0…1) — xarita qatlami kabi og'ir so'rovlarda
   * foizni silliq ko'tarish uchun. Server hajmni bermasa `-1` keladi.
   */
  onProgress?: (ratio: number) => void;
}

/**
 * Access-token yangilash.
 *
 * Token muddati qisqa (15 daqiqa), shuning uchun uzoq ochiq turgan kabinetda
 * so'rovlar 401 bilan qaytishi normal holat: shu funksiya refresh-cookie orqali
 * yangi token oladi va so'rov bir marta qaytariladi. Bir vaqtda bir nechta
 * so'rov 401 olsa ham refresh faqat bitta marta yuboriladi (`pendingRefresh`).
 */
let pendingRefresh: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  if (!pendingRefresh) {
    const csrf = readCookie("sf_csrf");
    pendingRefresh = fetch(`${BASE_URL}/auth/refresh/`, {
      method: "POST",
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

/** Refresh urinishi ma'nosiz bo'lgan endpointlar (sessiyaning o'zi shu yerda boshqariladi). */
function isAuthPath(path: string): boolean {
  return path.startsWith("/auth/login/") || path.startsWith("/auth/refresh/") ||
    path.startsWith("/auth/logout/") || path.startsWith("/auth/oneid/");
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return sendRequest<T>(path, options, !isAuthPath(path));
}

async function sendRequest<T>(
  path: string,
  options: RequestOptions,
  allowRefresh: boolean,
): Promise<T> {
  const { method = "GET", body, signal, lang, onProgress } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (lang) headers["Accept-Language"] = lang;
  if (method !== "GET") {
    const csrf = readCookie("sf_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(0, "Server bilan aloqa yo'q.", "offline");
  }

  if (response.status === 204) return undefined as T;

  const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
  const payload =
    onProgress && response.ok && response.body
      ? await readWithProgress(response, onProgress, isJson)
      : isJson
        ? await response.json()
        : await response.text();

  if (!response.ok) {
    const { message, code } = readError(isJson ? payload : null, response.status);
    /* Token eskirgan bo'lsa — yangilab, so'rovni bir marta takrorlaymiz. */
    if (response.status === 401 && allowRefresh) {
      if (await refreshSession()) return sendRequest<T>(path, options, false);
      /* Sessiya butunlay tugagan (refresh ham rad etildi): interfeys kirish
         sahifasiga qaytishi uchun xabar beramiz. */
      window.dispatchEvent(new CustomEvent("sf:unauthorized"));
    }
    throw new ApiError(response.status, message, code, payload);
  }

  return payload as T;
}

/**
 * Javobni oqim sifatida o'qiydi va yuklangan ulushni xabar qiladi.
 *
 * `Content-Length` bo'lmasa (siqilgan uzatish) ulush o'rniga `-1` yuboriladi —
 * bunday holatda chaqiruvchi soxta o'sish ko'rsatmasligi kerak.
 */
async function readWithProgress(
  response: Response,
  onProgress: (ratio: number) => void,
  isJson: boolean,
): Promise<unknown> {
  const total = Number(response.headers.get("content-length") || 0);
  const reader = response.body!.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress(total > 0 ? Math.min(loaded / total, 1) : -1);
  }

  const merged = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  const text = new TextDecoder().decode(merged);
  onProgress(1);
  return isJson ? JSON.parse(text) : text;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  del: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "DELETE", body }),
};

/**
 * Fayl bilan yuboriladigan so'rov (multipart) — superadmin panelida rasm va
 * hujjat maydonlari uchun. JSON klientdan farqi: `Content-Type` ni brauzer
 * o'zi qo'yadi (chegara belgisi bilan), qolgan qoidalar bir xil.
 */
/**
 * POST so'rov yuborib, javobni fayl sifatida saqlaydi (masalan hisobot
 * eksporti — `application/vnd...`, JSON emas). `apiUpload`ning "yuklab
 * olish" varianti: brauzer xotirasidagi vaqtinchalik havola orqali
 * `<a download>` bosiladi, so'ng darhol tozalanadi.
 */
export async function apiDownload(path: string, body: unknown): Promise<void> {
  const csrf = readCookie("sf_csrf");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (csrf) headers["X-CSRF-Token"] = csrf;

  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
    const payload = isJson ? await response.json() : null;
    const { message, code } = readError(payload, response.status);
    throw new ApiError(response.status, message, code, payload);
  }

  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? "hisobot";

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function apiUpload<T>(
  path: string,
  form: FormData,
  method: "POST" | "PATCH" = "PATCH",
): Promise<T> {
  const csrf = readCookie("sf_csrf");
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: csrf ? { "X-CSRF-Token": csrf, Accept: "application/json" } : { Accept: "application/json" },
      credentials: "include",
      body: form,
    });
  } catch {
    throw new ApiError(0, "Server bilan aloqa yo'q.", "offline");
  }
  const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
  const payload = isJson ? await response.json() : await response.text();
  if (!response.ok) {
    const { message, code } = readError(isJson ? payload : null, response.status);
    throw new ApiError(response.status, message, code, payload);
  }
  return payload as T;
}

/**
 * `apiUpload` ning yuklanish foizini beradigan varianti (XHR — `fetch` yuborish
 * jarayonini kuzatib bo'lmaydi). `onProgress` 0…1 oralig'ida chaqiriladi.
 */
export function apiUploadWithProgress<T>(
  path: string,
  form: FormData,
  onProgress: (ratio: number) => void,
  method: "POST" | "PATCH" = "POST",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${BASE_URL}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    const csrf = readCookie("sf_csrf");
    if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "Server bilan aloqa yo'q.", "offline"));
    xhr.ontimeout = xhr.onerror;
    xhr.onload = () => {
      const isJson = (xhr.getResponseHeader("content-type") ?? "").includes("application/json");
      let payload: unknown = xhr.responseText;
      if (isJson) {
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          payload = null;
        }
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const { message, code } = readError(isJson ? payload : null, xhr.status);
        reject(new ApiError(xhr.status, message, code, payload));
        return;
      }
      onProgress(1);
      resolve(payload as T);
    };
    xhr.send(form);
  });
}
